import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, ClipboardList, History, IndianRupee, LayoutDashboard, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/AuthContext";
import { riderApi } from "@/features/delivery/api";
import { LoadingScreen } from "@/components/common/LoadingScreen";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NotificationEnableButton } from "@/components/notifications/NotificationEnableButton";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

const NAV = [
    { to: ROUTES.riderDashboard, label: "Home", icon: LayoutDashboard },
    { to: ROUTES.riderActive, label: "Active", icon: ClipboardList },
    { to: ROUTES.riderEarnings, label: "Earnings", icon: IndianRupee },
    { to: ROUTES.riderHistory, label: "History", icon: History },
];

export function RiderLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const { data: rider, isLoading, isError } = useQuery({
        queryKey: ["rider", "me"],
        queryFn: riderApi.me,
        retry: false,
        refetchInterval: 15000,
    });

    const toggleMut = useMutation({
        mutationFn: async (val: boolean) => {
            // Best-effort attach location so admin auto-assign has fresh coords.
            const coords = await new Promise<{ lat: number; lng: number } | undefined>((resolve) => {
                if (!navigator.geolocation) return resolve(undefined);
                navigator.geolocation.getCurrentPosition(
                    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
                    () => resolve(undefined),
                    { timeout: 3000 },
                );
            });
            return riderApi.toggleOnline(val, coords);
        },
        onSuccess: (r) => {
            qc.setQueryData(["rider", "me"], r);
            toast.success(r.is_online ? "You're online — accepting orders" : "You're offline");
        },
        onError: () => toast.error("Couldn't update status"),
    });

    if (isLoading) return <LoadingScreen label="Loading rider profile…" />;
    if (isError || !rider) {
        navigate(ROUTES.riderLogin, { replace: true });
        return null;
    }

    const onLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate(ROUTES.riderLogin);
    };

    return (
        <div data-testid="rider-layout" className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 pb-24">
            <header className="bg-ink text-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-4">
                    <div className="flex items-center gap-3">
                        {rider.photo_url ? (
                            <img src={rider.photo_url} alt="" className="h-11 w-11 rounded-full object-cover" />
                        ) : (
                            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand text-white">
                                <Bike className="h-5 w-5" />
                            </span>
                        )}
                        <div>
                            <p className="text-xs uppercase tracking-widest text-white/50">TSGo Rider</p>
                            <p className="text-sm font-bold">{rider.full_name || user?.email}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <NotificationEnableButton />
                        <NotificationBell />
                        <button
                            onClick={() => toggleMut.mutate(!rider.is_online)}
                            disabled={toggleMut.isPending}
                            data-testid="rider-online-toggle"
                            className={cn(
                                "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition",
                                rider.is_online
                                    ? "bg-emerald-500 text-white"
                                    : "bg-white/10 text-white/80 hover:bg-white/20",
                            )}
                        >
                            <span className={cn("h-2 w-2 rounded-full", rider.is_online ? "bg-white" : "bg-white/50")} />
                            {rider.is_online ? "ONLINE" : "OFFLINE"}
                        </button>
                        <button
                            onClick={onLogout}
                            data-testid="rider-logout"
                            className="grid h-9 w-9 place-items-center rounded-lg bg-white/10 hover:bg-white/20"
                            title="Sign out"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-4 py-5">
                <Outlet />
            </main>

            <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-white/95 backdrop-blur">
                <div className="mx-auto grid max-w-3xl grid-cols-4">
                    {NAV.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={`rider-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                cn(
                                    "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition",
                                    isActive ? "text-brand" : "text-muted-foreground hover:text-foreground",
                                )
                            }
                        >
                            <Icon className="h-5 w-5" />
                            {label}
                        </NavLink>
                    ))}
                </div>
            </nav>
        </div>
    );
}

export default RiderLayout;
